import { z } from "zod";
/*
 * Canvases: self-contained HTML pages the Twin designs in a channel (a mini
 * site, a slide deck or an interactive lookbook). The source is an MLX
 * artifact of kind "html". It never runs in the app's origin: the tenant
 * assembles it with the canvas kit and the images it names, and every viewer
 * shows the result in a sandboxed frame inside the platform's shell
 * (canvasShellDocument), both under CANVAS_DOCUMENT_POLICY, so a page can
 * neither reach the network, send itself elsewhere, nor read the viewer's session.
 */
/** What a canvas is. "site" is kept for canvases made before the others; new ones are not offered it. */
export const CANVAS_FORMATS = ["deck", "explainer", "story", "lookbook", "site"];
/** The formats the Twin chooses from when it makes a canvas. */
export const CANVAS_OFFERED_FORMATS = ["deck", "explainer", "story", "lookbook"];
export const canvasFormatSchema = z.enum(CANVAS_FORMATS);
/** The most a canvas's own HTML may hold, before images are inlined. */
export const CANVAS_MAX_SOURCE_BYTES = 1024 * 1024;
/** A workspace file a canvas shows is named `mlx-file:<file id>`; the tenant inlines it. */
export const CANVAS_FILE_REF_PREFIX = "mlx-file:";
export const CANVAS_FILE_REF_PATTERN = /mlx-file:([A-Za-z0-9_-]{1,200})/gu;
/** The most images, and the most bytes of them, one assembled canvas inlines. */
export const CANVAS_MAX_IMAGES = 60;
export const CANVAS_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
export const CANVAS_MAX_IMAGES_BYTES = 24 * 1024 * 1024;
/**
 * What a canvas's frames may do. Inline style only, and inline script for the
 * platform's own (an assembled canvas narrows it to the kit's runtime by hash,
 * CANVAS_KIT_POLICY); images, fonts and media only from data the document
 * already holds; no requests, forms, frames, workers or base URL. A document
 * cannot govern its own navigation, so the shell around it enforces this policy
 * too: its frame-src stops the canvas, and any frame it makes, from going anywhere.
 */
export const CANVAS_DOCUMENT_POLICY = [
    "default-src 'none'",
    "script-src 'unsafe-inline'",
    "style-src 'unsafe-inline'",
    "img-src data: blob:",
    "font-src data:",
    "media-src data: blob:",
    "connect-src 'none'",
    "form-action 'none'",
    "frame-src 'none'",
    "worker-src 'none'",
    "base-uri 'none'",
    // WebRTC is outside connect-src; browsers that know this directive refuse it (the kit's guard covers the rest).
    "webrtc 'block'",
].join("; ");
/**
 * The frame's sandbox: scripts run in an opaque origin (never
 * allow-same-origin) and print dialogs work. No popups: a canvas cannot open
 * a window after a click, and a link a person clicks is opened by the host,
 * which the kit asks (mlx-canvas:open).
 */
export const CANVAS_FRAME_SANDBOX = "allow-scripts allow-modals";
/**
 * Relays messages between the canvas and its host, so each still talks to its parent, and passes
 * keyboard focus on to the canvas when the host focuses the frame, so a deck takes the arrow keys.
 * The hand-off waits a turn: neither Chromium nor WebKit moves focus on inside the focus event.
 */
const CANVAS_SHELL_SCRIPT = `(function(){var f=document.querySelector("iframe");`
    + `addEventListener("message",function(e){var c=f.contentWindow;if(!c)return;`
    + `if(e.source===c)parent.postMessage(e.data,"*");else if(e.source===parent)c.postMessage(e.data,"*")});`
    + `addEventListener("focus",function(){setTimeout(function(){if(document.activeElement!==f)f.focus()},0)})})();`;
function escapeAttribute(value) {
    return value.replace(/&/gu, "&amp;").replace(/"/gu, "&quot;");
}
/**
 * The platform's page a viewer's frame holds, with the canvas in a frame of its own. Where a frame
 * may go is decided by the page that holds it, and the app's own policy is only reported, so this
 * shell is that page: under the canvas policy (frame-src 'none'), the canvas cannot navigate itself
 * or a frame it makes, by script, meta refresh or link. The canvas inherits the policy as well.
 */
export function canvasShellDocument(document, title) {
    return [
        "<!doctype html>",
        "<meta charset=\"utf-8\">",
        `<meta http-equiv="Content-Security-Policy" content="${CANVAS_DOCUMENT_POLICY}">`,
        "<meta name=\"referrer\" content=\"no-referrer\">",
        "<style>html,body{margin:0;height:100%;overflow:hidden;background:transparent}iframe{display:block;width:100%;height:100%;border:0}</style>",
        `<iframe sandbox="${CANVAS_FRAME_SANDBOX}" referrerpolicy="no-referrer" title="${escapeAttribute(title)}" srcdoc="${escapeAttribute(document)}"></iframe>`,
        `<script>${CANVAS_SHELL_SCRIPT}</script>`,
    ].join("\n");
}
/** Header policy for an assembled document served directly: the same rules, sandboxed even at the top level. */
export const CANVAS_DOCUMENT_HEADER_POLICY = `sandbox ${CANVAS_FRAME_SANDBOX}; ${CANVAS_DOCUMENT_POLICY}`;
/** Headers every assembled canvas response carries. */
export function canvasDocumentHeaders(extra = {}) {
    return {
        "content-type": "text/html; charset=utf-8",
        "content-security-policy": CANVAS_DOCUMENT_HEADER_POLICY,
        "x-content-type-options": "nosniff",
        "referrer-policy": "no-referrer",
        "cache-control": "private, no-store",
        ...extra,
    };
}
// Checks: what the automatic checks found on a version, recorded on it once its write returns.
/** One part of a version as its fact-check left it: the findings still open on it, or null when it was not checked. */
export const canvasCheckedPartSchema = z.object({
    id: z.string().min(1).max(200),
    label: z.string().max(200),
    findings: z.number().int().nonnegative().nullable(),
}).strict();
export const canvasVersionChecksSchema = z.object({
    checkedAt: z.number(),
    /** The side-pane view: how many parts it found a measure problem in, or why it could not run. */
    view: z.union([
        z.object({ problemParts: z.number().int().nonnegative() }).strict(),
        z.object({ error: z.string().max(300) }).strict(),
    ]),
    /** Each part with words, as the fact-check left it, or why the fact-check could not run. */
    facts: z.union([
        z.object({ parts: z.array(canvasCheckedPartSchema).max(500) }).strict(),
        z.object({ error: z.string().max(300) }).strict(),
    ]),
}).strict();
/** A canvas document's version checks, summarised, as URI-encoded JSON (a header, so never the parts). */
export const CANVAS_CHECKS_HEADER = "x-mlx-canvas-checks";
/**
 * What a reader is told of a version's checks. "unchecked": none recorded (written before checks
 * were, or its turn stopped first). "partial": the fact-check could not run (notChecked null), or
 * some parts were not checked.
 */
export const canvasChecksSummarySchema = z.discriminatedUnion("state", [
    z.object({ state: z.literal("clear") }).strict(),
    z.object({ state: z.literal("findings"), findings: z.number().int().positive() }).strict(),
    z.object({ state: z.literal("partial"), findings: z.number().int().nonnegative(), notChecked: z.number().int().positive().nullable() }).strict(),
    z.object({ state: z.literal("unchecked") }).strict(),
]);
export function canvasChecksSummary(checks) {
    if (!checks)
        return { state: "unchecked" };
    if ("error" in checks.facts)
        return { state: "partial", findings: 0, notChecked: null };
    const findings = checks.facts.parts.reduce((sum, part) => sum + (part.findings ?? 0), 0);
    const notChecked = checks.facts.parts.filter((part) => part.findings === null).length;
    if (notChecked > 0)
        return { state: "partial", findings, notChecked };
    return findings > 0 ? { state: "findings", findings } : { state: "clear" };
}
/** One version of a canvas, as its list names it: which, when it was written, and what the checks found on it. Never its page. */
export const canvasVersionSchema = z.object({
    versionId: z.string().min(1),
    version: z.number().int().positive(),
    createdAt: z.number(),
    /** What the automatic checks found on it; null for a version written before checks were recorded. */
    checks: canvasChecksSummarySchema.nullable(),
    /** The version a canvas opens at: its latest. */
    current: z.boolean(),
}).strict();
/** A canvas's versions, newest first. */
export const listCanvasVersionsResponseSchema = z.object({ versions: z.array(canvasVersionSchema).max(100) }).strict();
/**
 * On a canvas's version list: "1" where the reader may make an earlier version the latest (the person
 * whose session wrote the canvas, or an organisation admin). A header, so a reader that does not know of
 * it reads the list as before.
 */
export const CANVAS_RESTORE_HEADER = "x-mlx-canvas-may-restore";
/** Makes an earlier version of a canvas its latest again, as a new version that is a copy of it. */
export const restoreCanvasVersionInputSchema = z.object({
    /** The earlier version to bring back. */
    versionId: z.string().trim().min(1).max(200),
    /** The canvas's latest version as the reader was shown it: refused where a newer one has been written since. */
    latestVersionId: z.string().trim().min(1).max(200),
}).strict();
/** The new latest version, under its own number, and the number of the version it is a copy of. */
export const restoreCanvasVersionResponseSchema = z.object({
    version: canvasVersionSchema,
    restoredFrom: z.number().int().positive(),
}).strict();
/** How many images an assembled canvas left out (over a limit, missing or unreadable). */
export const CANVAS_OMITTED_IMAGES_HEADER = "x-mlx-canvas-omitted-images";
/*
 * Messages between a canvas's kit runtime and the frame that hosts it. The
 * host checks that a message came from its own frame; the kit checks that a
 * message came from its parent.
 */
export const canvasKitMessageSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("mlx-canvas:ready"), format: canvasFormatSchema.nullable(), slides: z.number().int().nonnegative() }),
    z.object({
        type: z.literal("mlx-canvas:layout"),
        width: z.number(),
        height: z.number(),
        overflowX: z.boolean(),
    }),
    z.object({
        type: z.literal("mlx-canvas:anchor"),
        anchorId: z.string().min(1).max(200),
        anchorLabel: z.string().max(200),
    }),
    z.object({ type: z.literal("mlx-canvas:pin"), annotationId: z.string().min(1).max(200) }),
    z.object({ type: z.literal("mlx-canvas:anchors"), anchorIds: z.array(z.string().max(200)).max(2000) }),
    /** A link the person clicked, for the host to open in a new tab. */
    z.object({ type: z.literal("mlx-canvas:open"), href: z.string().min(1).max(4096) }),
    /** The slide a deck now shows, counted from 0, so a new version can open where the reader was. */
    z.object({ type: z.literal("mlx-canvas:slide"), index: z.number().int().nonnegative().max(10_000) }),
]);
// Annotations: comments pinned to one element of a canvas, kept across versions.
export const canvasAnnotationStatusSchema = z.enum(["open", "resolved"]);
export const canvasAnnotationSchema = z.object({
    id: z.string().min(1),
    artifactId: z.string().min(1),
    /** The version the comment was made on. */
    versionId: z.string().min(1),
    version: z.number().int().positive(),
    anchorId: z.string().min(1),
    anchorLabel: z.string(),
    body: z.string(),
    /** The people the comment mentions as @Name in its body; each was notified. */
    mentionedUserIds: z.array(z.string().min(1)).default([]),
    status: canvasAnnotationStatusSchema,
    authorId: z.string().min(1),
    authorName: z.string().nullable(),
    createdAt: z.number(),
    updatedAt: z.number(),
    resolvedAt: z.number().nullable(),
    resolvedBy: z.string().nullable(),
}).strict();
/** The most people a comment may mention, and the most a canvas offers to mention. */
export const CANVAS_COMMENT_MAX_MENTIONS = 20;
export const CANVAS_MENTIONABLE_MAX = 500;
export const listCanvasAnnotationsResponseSchema = z.object({
    annotations: z.array(canvasAnnotationSchema),
    /** The people who can read the canvas, and so can be mentioned in a comment on it. */
    mentionableUserIds: z.array(z.string().min(1)).max(CANVAS_MENTIONABLE_MAX).default([]),
}).strict();
export const createCanvasAnnotationInputSchema = z.object({
    versionId: z.string().trim().min(1).max(200),
    anchorId: z.string().trim().min(1).max(200),
    anchorLabel: z.string().trim().max(200),
    body: z.string().trim().min(1).max(4000),
    /** People named as @Name in the body; only those who can read the canvas are kept and notified. */
    mentionedUserIds: z.array(z.string().trim().min(1).max(200)).max(CANVAS_COMMENT_MAX_MENTIONS).optional(),
}).strict();
export const patchCanvasAnnotationInputSchema = z.object({
    status: canvasAnnotationStatusSchema.optional(),
    body: z.string().trim().min(1).max(4000).optional(),
}).strict().refine((value) => value.status !== undefined || value.body !== undefined, {
    message: "Change the status or the comment.",
});
export const canvasAnnotationResponseSchema = z.object({ annotation: canvasAnnotationSchema }).strict();
/**
 * The organisation a canvas is shown for, URI-encoded, as the app read it from its own session: the
 * byline and sign-off name it. Display only; a verified Clerk organisation name takes its place.
 */
export const CANVAS_ORGANISATION_NAME_HEADER = "x-mlx-organisation-name";
// Publications: a frozen copy of one version, readable by anyone with its link.
export const CANVAS_PUBLICATION_MAX_DAYS = 90;
export const CANVAS_PUBLICATION_DEFAULT_DAYS = 30;
export const CANVAS_PUBLICATION_GRANT_HEADER = "x-mlx-canvas-grant";
/** How many of a link's latest opens it lists. Anyone may hold a link, so an open is a time, never who. */
export const CANVAS_PUBLICATION_RECENT_OPENS = 20;
export const canvasPublicationSchema = z.object({
    id: z.string().min(1),
    artifactId: z.string().min(1),
    versionId: z.string().min(1),
    version: z.number().int().positive(),
    title: z.string(),
    passwordProtected: z.boolean(),
    createdBy: z.string().min(1),
    createdAt: z.number(),
    expiresAt: z.number(),
    revokedAt: z.number().nullable(),
    viewCount: z.number().int().nonnegative(),
    lastViewedAt: z.number().nullable(),
    omittedImages: z.number().int().nonnegative(),
    /** When the link was last opened, newest first. */
    recentOpens: z.array(z.number()).max(CANVAS_PUBLICATION_RECENT_OPENS).default([]),
    /** How many times someone entered a wrong password. */
    passwordRejections: z.number().int().nonnegative().default(0),
}).strict();
export const listCanvasPublicationsResponseSchema = z.object({
    /** Whether a link can be made: this server publishes, and the organisation has public links on. */
    available: z.boolean(),
    publications: z.array(canvasPublicationSchema),
}).strict();
// Public links, by organisation: an admin turns them on and sees every live one.
/** A live link of the organisation's, as its admins see it. */
export const organisationCanvasLinkSchema = z.object({
    id: z.string().min(1),
    artifactId: z.string().min(1),
    title: z.string(),
    version: z.number().int().positive(),
    createdBy: z.string().min(1),
    createdAt: z.number(),
    expiresAt: z.number(),
    passwordProtected: z.boolean(),
    viewCount: z.number().int().nonnegative(),
    lastViewedAt: z.number().nullable(),
}).strict();
export const canvasLinkSettingsResponseSchema = z.object({
    /** Whether this server publishes canvases at all (MLX_CANVAS_PUBLIC_LINKS_ENABLED). */
    serverAllows: z.boolean(),
    /** Whether the organisation has public links on: off for an organisation new to the workspace. */
    allowPublicLinks: z.boolean(),
    canEdit: z.boolean(),
    updatedAt: z.number().nullable(),
    /** Its live links, newest first, for an admin; empty for anyone else. */
    liveLinks: z.array(organisationCanvasLinkSchema).max(500),
}).strict();
export const putCanvasLinkSettingsInputSchema = z.object({ allowPublicLinks: z.boolean() }).strict();
export const createCanvasPublicationInputSchema = z.object({
    /** The version to publish; the current one when left out. */
    versionId: z.string().trim().min(1).max(200).optional(),
    expiresInDays: z.number().int().min(1).max(CANVAS_PUBLICATION_MAX_DAYS).default(CANVAS_PUBLICATION_DEFAULT_DAYS),
    password: z.string().min(8).max(200).optional(),
}).strict();
export const createCanvasPublicationResponseSchema = z.object({
    publication: canvasPublicationSchema,
    /** The link's secret, shown once: only its hash is kept. */
    token: z.string().min(32),
}).strict();
export const canvasPublicationResponseSchema = z.object({ publication: canvasPublicationSchema }).strict();
/** Sets a live link's password, or removes it with null. Anyone who opened it with the old one must enter the new one. */
export const updateCanvasPublicationInputSchema = z.object({
    password: z.string().min(8).max(200).nullable(),
}).strict();
export const CANVAS_PUBLICATION_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/u;
export const openCanvasPublicationInputSchema = z.object({
    password: z.string().min(1).max(200).optional(),
}).strict();
export const openCanvasPublicationResponseSchema = z.object({
    title: z.string(),
    organisationName: z.string().nullable(),
    expiresAt: z.number(),
    passwordRequired: z.boolean(),
    /** Opens the document until it expires; absent while a password is still required. */
    grant: z.string().min(1).optional(),
}).strict();
/** The address of a published canvas on the app that serves it. */
export function canvasPublicationPath(token) {
    return `/share/canvas/${encodeURIComponent(token)}`;
}
